import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { FiExternalLink, FiChevronLeft } from "react-icons/fi";
import ApiServices from "../services/apiservices";
import CustomSpinner from "../components/customspinner";

export default function JobDetails() {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check if application deadline has passed
  const isDeadlinePassed = job?.applicationDeadline
    ? new Date(job.applicationDeadline) < new Date()
    : false;

  // Helper to render text with newlines as a bulleted list
  const renderAsList = (text) => {
    if (!text) return null;

    const items = text
      .split("\n")
      .map((item) => item.trim())
      .filter((item) => item.length > 0);

    // Render plain paragraph if it's not a list
    if (items.length === 1 && !/^[-•*]/.test(items[0])) {
      return (
        <div className="text-gray-700 leading-7 whitespace-pre-wrap break-words">
          {text}
        </div>
      );
    }

    return (
      <ul className="list-disc list-outside ml-5 space-y-3 text-gray-700 leading-7">
        {items.map((item, idx) => (
          <li key={idx}>{item.replace(/^[-•*]\s*/, "")}</li>
        ))}
      </ul>
    );
  };

  useEffect(() => {
    const fetchJobDetails = async () => {
      try {
        const res = await ApiServices.getRequest(`/jobs/fetch.php?id=${id}`);

        if (res.success && res.job) {
          setJob(res.job);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchJobDetails();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <CustomSpinner />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4 text-center">
        <h2 className="text-2xl font-bold text-gray-800">
          Job Not Found
        </h2>

        <Link
          to="/search-jobs"
          className="mt-4 text-blue-600 hover:underline"
        >
          Back to Careers
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 font-sans text-gray-800 pb-16">
      {/* Header */}
      <div className="bg-black text-white pt-12 md:pt-16 pb-10 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
          
          {/* Left Side */}
          <div className="w-full">
            <Link
              to="/search-jobs"
              className="inline-flex items-center text-gray-400 hover:text-white mb-5 transition-colors text-sm sm:text-base"
            >
              <FiChevronLeft className="mr-2" />
              Back to open roles
            </Link>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-4 break-words">
              {job.title}
            </h1>

            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 text-sm sm:text-base text-gray-300">
              <span>
                Location:{" "}
                <span className="font-semibold text-white">
                  {job.location}
                </span>
              </span>

              <span className="hidden sm:inline">|</span>

              <span>
                Team:{" "}
                <span className="font-semibold text-white">
                  {job.team}
                </span>
              </span>

              {job.jobType && (
                <>
                  <span className="hidden sm:inline">|</span>

                  <span>
                    Job Type:{" "}
                    <span className="font-semibold text-white">
                      {job.jobType}
                    </span>
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Right Side */}
          <div className="w-full lg:w-auto">
            {isDeadlinePassed ? (
              <button
                disabled
                className="w-full lg:w-auto inline-flex items-center justify-center gap-2 bg-gray-500 text-white font-bold py-3 px-6 rounded-lg cursor-not-allowed opacity-80"
              >
                No Longer Accepting Applications
              </button>
            ) : (
              <a
                href="mailto:ubavictor24@gmail.com"
                className="w-full lg:w-auto inline-flex items-center justify-center gap-2 bg-yellow-400 hover:bg-yellow-500 text-black font-bold py-3 px-6 rounded-lg transition-colors"
              >
                Apply Now
                <FiExternalLink />
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="bg-pink-300 border-b border-pink-400 overflow-x-auto">
        <div className="max-w-5xl mx-auto flex items-center gap-6 px-4 sm:px-6 py-4 whitespace-nowrap text-sm sm:text-base font-semibold text-black">
          <a href="#overview" className="hover:underline">
            Overview
          </a>

          {job.responsibilities && (
            <a href="#responsibilities" className="hover:underline">
              Responsibilities
            </a>
          )}

          {job.qualifications && (
            <a href="#qualifications" className="hover:underline">
              Qualifications
            </a>
          )}

          {job.howToApply && (
            <a href="#how-to-apply" className="hover:underline">
              How to Apply
            </a>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="bg-white mt-6 sm:mt-8 shadow-sm rounded-xl p-5 sm:p-8 md:p-10">
          
          {/* Overview */}
          {job.description && (
            <section id="overview" className="mb-12 scroll-mt-24">
              <h2 className="text-2xl sm:text-3xl font-bold mb-5 text-black">
                Overview
              </h2>

              <div className="text-gray-700 leading-7 whitespace-pre-wrap break-words">
                {job.description}
              </div>
            </section>
          )}

          {/* Responsibilities */}
          {job.responsibilities && (
            <section
              id="responsibilities"
              className="mb-12 scroll-mt-24"
            >
              <h2 className="text-2xl sm:text-3xl font-bold mb-5 text-black">
                Responsibilities
              </h2>

              {renderAsList(job.responsibilities)}
            </section>
          )}

          {/* Qualifications */}
          {job.qualifications && (
            <section
              id="qualifications"
              className="mb-12 scroll-mt-24"
            >
              <h2 className="text-2xl sm:text-3xl font-bold mb-5 text-black">
                Qualifications
              </h2>

              {renderAsList(job.qualifications)}
            </section>
          )}

          {/* How To Apply */}
          {job.howToApply && (
            <section
              id="how-to-apply"
              className="mb-12 scroll-mt-24"
            >
              <h2 className="text-2xl sm:text-3xl font-bold mb-5 text-black">
                How to Apply
              </h2>

              {renderAsList(job.howToApply)}
            </section>
          )}

          {/* Other Details */}
          {job.otherDetails && (
            <section className="mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold mb-5 text-black">
                Other Details
              </h2>

              {renderAsList(job.otherDetails)}
            </section>
          )}

          {/* Deadline */}
          {job.applicationDeadline && (
            <div className="p-4 sm:p-5 bg-gray-50 border-l-4 border-yellow-400 rounded-lg">
              <p className="font-semibold text-gray-800 text-sm sm:text-base">
                Application Deadline:
                <span className="font-normal">
                  {" "}
                  {job.applicationDeadline}
                </span>
              </p>

              {isDeadlinePassed && (
                <p className="text-red-700 mt-2 text-sm sm:text-base">
                  No longer accepting applications
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}